import imagePlaceholder from '@/assets/placeholder.png';
import GoBackMenu from '@/components/GoBackNavbar';
import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { useQuestionnaires } from '@/hooks/questionnaire/useQuestionnaires';
import { useCourseStudentView } from '@/hooks/useCourseStudentView';
import { EvaluationLesson } from '@/models/Lesson';
import { LessonEnrollment } from '@/models/LessonEnrollment';
import { courseService } from '@/services/CourseService';
import { lmsCourseService } from '@/services/LMSCourseService';
import { ApplicationState } from '@/store';
import { LocaleData } from '@/translations/LocaleData';
import React, { useEffect, useReducer } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import './style.scss';

enum ConditionsToNavigate {
    PENDING_QUESTIONNAIRES = 'PENDING_QUESTIONNAIRES',
    ATTEMPTS_LEFT = 'ATTEMPTS_LEFT',
}

interface ConditionsToNavigateState {
    hasPendingQuestionnaires: boolean;
    hasAttemptsLeft: boolean;
}

interface ConditionsToNavigateAction {
    type: ConditionsToNavigate;
    payload: boolean;
}

const initialState: ConditionsToNavigateState = {
    hasPendingQuestionnaires: false,
    hasAttemptsLeft: false,
};

const conditionsToNavigateReducer = (state: ConditionsToNavigateState, action: ConditionsToNavigateAction) => {
    switch (action.type) {
        case ConditionsToNavigate.PENDING_QUESTIONNAIRES: {
            return { ...state, hasPendingQuestionnaires: action.payload };
        }
        case ConditionsToNavigate.ATTEMPTS_LEFT: {
            return { ...state, hasAttemptsLeft: action.payload };
        }
        default: {
            return initialState;
        }
    }
};

const EvaluationPage: React.FC = () => {
    const lessonId: number = Number(useParams<{ lessonId: string }>().lessonId);
    const courseId: number = Number(useParams<{ courseId: string }>().courseId);
    const classEnrollmentId: number = Number(useParams<{ enrollmentId: string }>().enrollmentId);

    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);

    const [condition, action] = useReducer(conditionsToNavigateReducer, initialState);

    const {
        course,
        courseEnrollment,
        classEnrollment,
        loading: courseStudentViewLoading,
        getLessonEnrollment,
        clearCourseStudentView,
        refresh,
    } = useCourseStudentView({
        moduleId: Constants.LMS_MODULE_ID,
        courseId,
        classEnrollmentId,
    });

    const lessonEnrollment: LessonEnrollment = getLessonEnrollment(lessonId);

    const evaluationLesson = lessonEnrollment.lesson as EvaluationLesson;

    const { questionnaires = [], loading: questionnairesLoading, clear: clearQuestionnaires } = useQuestionnaires({
        evaluationId: evaluationLesson?.evaluationId,
        classEnrollmentId,
    });

    const isCourseFromImport: boolean = Boolean(courseEnrollment.course?.dynamicAttributes?.createdFromIntegrationProcess);

    const history = useHistory();

    const clearAllData = (): void => {
        clearCourseStudentView();
        clearQuestionnaires();
    };

    const initEvaluation = (): void => {
        navigateToQuestionnaire();
    };

    const checkRemainingAttempts = (): void => {
        if (evaluationLesson) {
            const { attempts = 0, grade = 0 } = lessonEnrollment;
            const attemptsLeft = lmsCourseService.attemptsLeftInEvaluation(evaluationLesson, attempts, grade);
            action({ type: ConditionsToNavigate.ATTEMPTS_LEFT, payload: attemptsLeft === 0 });
        }
    };

    const checkPendinigQuestionnaires = (): void => {
        if (questionnaires.length > 0) {
            const pendingQuestionnaires = questionnaires.filter(({ score }) => score === null);
            action({ type: ConditionsToNavigate.PENDING_QUESTIONNAIRES, payload: !isCourseFromImport && pendingQuestionnaires.length > 0 });
        }
    };

    const checkConditionsToNavigate = (): void => {
        if (!condition.hasAttemptsLeft && !condition.hasPendingQuestionnaires) {
            return;
        }
        navigateToQuestionnaire();
    };

    const navigateToQuestionnaire = (): void => {
        if (classEnrollment?.id && evaluationLesson?.evaluationId) {
            clearAllData();

            const path = `/lms/enrollments/${classEnrollment.id}`
                .concat(`/courses/${courseId}`)
                .concat(`/lessons/${lessonId}`)
                .concat(`/evaluations/${evaluationLesson.evaluationId}`)
                .concat(`/class/${classEnrollment.id}`)
                .concat(`/questionnaire`);

            history.replace(path);
        }
    };

    const loadOnMount = (): void => {
        if (course.id) {
            refresh();
        }
    };

    useEffect(loadOnMount, []);

    useEffect(checkConditionsToNavigate, [condition]);

    useEffect(checkPendinigQuestionnaires, [isCourseFromImport, questionnaires]);

    useEffect(checkRemainingAttempts, [evaluationLesson, lessonEnrollment.id]);

    if (!classEnrollment || courseStudentViewLoading || questionnairesLoading) {
        return <Loader styles={{ height: 'auto' }} />;
    }

    return (
        <div className="evaluation-page">
            <GoBackMenu />

            <div className="list-bg" id={Constants.TOP_IMG_ID}>
                <img className="list-bg__img" src={lessonEnrollment.lesson?.mainImage || imagePlaceholder} />

                <div className="container list-bg__container">
                    <div className="list-bg__corner col-sm-10"></div>
                </div>
            </div>

            <div className="container">
                <div className="row">
                    <div className="col-sm-8 offset-sm-2 list-page__adjust">
                        <div className="list-content">
                            {evaluationLesson && (
                                <>
                                    <h1 className="list-content__title">{evaluationLesson.name}</h1>

                                    <div className="list-content__info">
                                        <div className="list-content__status">
                                            <span className="circle">
                                                {evaluationLesson.content.author && (
                                                    <>
                                                        {lessonEnrollment.lesson.content.author}
                                                        <i className="material-icons align-middle">{Constants.icons.CIRCLE}</i>
                                                    </>
                                                )}
                                                {strings.NOT_ATTEMPTED}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="list-content__status">
                                        <span>{`${strings.CLASS_ID} #${classEnrollment.programClass.id}`}</span>
                                    </div>

                                    <div className="list-content__icon">
                                        <label>
                                            <span>
                                                <i className="material-icons">{Constants.icons.CHART}</i>
                                            </span>
                                            {evaluationLesson.questionsAmount || ''} {strings.QUESTIONS}
                                        </label>
                                        {evaluationLesson.timeLimit > 0 && (
                                            <label>
                                                <span>
                                                    <i className="material-icons">{Constants.icons.ALARM}</i>
                                                </span>
                                                {courseService.formatWorkload(evaluationLesson.timeLimit, strings, true)}
                                            </label>
                                        )}
                                    </div>
                                </>
                            )}

                            <div className="list-content__tip-desc">{strings.EVALUATION_TIPS_1}</div>

                            <div className="list-content__tips">
                                <i className="material-icons-outlined">{Constants.icons.EDIT}</i>
                                <span>{strings.EVALUATION_TIPS_2}</span>
                            </div>

                            <div className="list-content__tips">
                                <i className="material-icons">{Constants.icons.RUN}</i>
                                <span>{strings.EVALUATION_TIPS_3}</span>
                            </div>

                            <div className="list-content__tips">
                                <i className="material-icons">{Constants.icons.ERROR_OUTLINE}</i>
                                <span>{strings.EVALUATION_TIPS_4}</span>
                            </div>

                            <div className="list-content__tips">
                                <i className="material-icons">{Constants.icons.ALARM}</i>
                                <span>{strings.EVALUATION_TIPS_5}</span>
                            </div>

                            <div className="list-content__controls">
                                <button className="btn btn-light mr-3" onClick={() => history.goBack()}>
                                    {strings.CANCEL}
                                </button>

                                <button className="btn btn-primary" disabled={!classEnrollment} onClick={initEvaluation}>
                                    {strings.START_EVALUATION}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EvaluationPage;
